<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import socialCommentAutomationsAPI from 'dashboard/api/socialCommentAutomations';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import SocialCommentAutomationDialog from './SocialCommentAutomationDialog.vue';
import ConnectionHealthPanel from './ConnectionHealthPanel.vue';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const isLoading = ref(true);
const automations = ref([]);
const togglingIds = ref({});
const deletingIds = ref({});
const dialogRef = ref(null);

const inboxes = computed(() => getters['inboxes/getInboxes'].value);

const eligibleInboxes = computed(() =>
  inboxes.value.filter(
    inbox =>
      inbox.channel_type === INBOX_TYPES.INSTAGRAM ||
      inbox.channel_type === INBOX_TYPES.FB
  )
);

const inboxLabel = inboxId => {
  const inbox = eligibleInboxes.value.find(item => item.id === inboxId);
  return inbox ? inbox.name : `#${inboxId}`;
};

const inboxChannelType = inboxId => {
  const inbox = inboxes.value.find(item => item.id === inboxId);
  return inbox ? inbox.channel_type : null;
};

const triggerLabel = trigger =>
  t(
    `INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.TRIGGERS.${(
      trigger || 'comment'
    ).toUpperCase()}`
  );

const loadAutomations = async () => {
  try {
    const { data } = await socialCommentAutomationsAPI.get();
    automations.value = data || [];
  } catch (error) {
    useAlert(
      t('INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.API.LOAD_ERROR')
    );
    automations.value = [];
  }
};

onMounted(async () => {
  isLoading.value = true;
  try {
    await Promise.all([store.dispatch('inboxes/get'), loadAutomations()]);
  } finally {
    isLoading.value = false;
  }
});

const openCreate = () => {
  dialogRef.value?.open();
};

const openEdit = rule => {
  dialogRef.value?.open(rule);
};

const handleSaved = () => {
  loadAutomations();
};

const toggleEnabled = async rule => {
  if (togglingIds.value[rule.id]) return;

  const nextEnabled = !rule.enabled;
  togglingIds.value = { ...togglingIds.value, [rule.id]: true };
  try {
    await socialCommentAutomationsAPI.update(rule.id, {
      enabled: nextEnabled,
    });
    rule.enabled = nextEnabled;
  } catch (error) {
    useAlert(
      t('INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.API.TOGGLE_ERROR')
    );
  } finally {
    const next = { ...togglingIds.value };
    delete next[rule.id];
    togglingIds.value = next;
  }
};

const deleteRule = async rule => {
  const confirmMessage = t(
    'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.DELETE_CONFIRM'
  );
  // eslint-disable-next-line no-alert
  if (!window.confirm(confirmMessage)) {
    return;
  }

  deletingIds.value = { ...deletingIds.value, [rule.id]: true };
  try {
    await socialCommentAutomationsAPI.delete(rule.id);
    useAlert(
      t('INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.API.DELETE_SUCCESS')
    );
    automations.value = automations.value.filter(item => item.id !== rule.id);
  } catch (error) {
    useAlert(
      t('INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.API.DELETE_ERROR')
    );
  } finally {
    const next = { ...deletingIds.value };
    delete next[rule.id];
    deletingIds.value = next;
  }
};
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="
      t('INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.LOADING')
    "
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.TITLE')"
        :description="
          t('INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.DESCRIPTION')
        "
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-6">
        <ConnectionHealthPanel />
        <div
          class="p-4 text-sm border rounded-md bg-n-alpha-2 border-n-weak text-n-slate-11"
        >
          {{ t('INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.INTRO') }}
        </div>

        <div class="flex items-center justify-between">
          <h3 class="text-base font-semibold text-n-slate-12">
            {{
              t('INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.LIST.TITLE')
            }}
          </h3>
          <Button
            icon="i-lucide-plus"
            :label="
              t('INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.LIST.ADD')
            "
            @click="openCreate"
          />
        </div>

        <div
          v-if="!eligibleInboxes.length"
          class="p-4 text-sm border border-dashed rounded-md text-n-slate-11 border-n-weak"
        >
          {{
            t(
              'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.NO_ELIGIBLE_INBOXES'
            )
          }}
        </div>

        <div
          v-if="!automations.length"
          class="p-6 text-sm text-center border border-dashed rounded-md text-n-slate-11 border-n-weak"
        >
          {{ t('INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.LIST.EMPTY') }}
        </div>

        <div v-else class="overflow-hidden border rounded-xl border-n-weak">
          <table class="min-w-full divide-y divide-n-weak">
            <thead class="bg-n-alpha-1">
              <tr>
                <th
                  class="px-4 py-3 text-xs font-semibold tracking-wide text-left uppercase text-n-slate-10"
                >
                  {{
                    t(
                      'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.LIST.COL_NAME'
                    )
                  }}
                </th>
                <th
                  class="px-4 py-3 text-xs font-semibold tracking-wide text-left uppercase text-n-slate-10"
                >
                  {{
                    t(
                      'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.LIST.COL_KEYWORDS'
                    )
                  }}
                </th>
                <th
                  class="px-4 py-3 text-xs font-semibold tracking-wide text-left uppercase text-n-slate-10"
                >
                  {{
                    t(
                      'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.LIST.COL_ENABLED'
                    )
                  }}
                </th>
                <th class="px-4 py-3" />
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr
                v-for="rule in automations"
                :key="rule.id"
                class="transition-colors hover:bg-n-alpha-1"
              >
                <td class="px-4 py-3 text-sm font-medium text-n-slate-12">
                  <div class="flex flex-col">
                    <div class="flex items-center gap-1.5">
                      <span
                        class="size-4 text-n-slate-11"
                        :class="[
                          inboxChannelType(rule.inbox_id) ===
                          INBOX_TYPES.INSTAGRAM
                            ? 'i-lucide-instagram'
                            : 'i-lucide-facebook',
                        ]"
                      />
                      <span>{{ rule.name }}</span>
                      <span
                        class="px-2 py-0.5 text-xs rounded-full bg-n-slate-3 text-n-slate-11 whitespace-nowrap"
                      >
                        {{ triggerLabel(rule.trigger) }}
                      </span>
                    </div>
                    <span class="text-xs text-n-slate-11">
                      {{ inboxLabel(rule.inbox_id) }}
                    </span>
                  </div>
                </td>
                <td class="px-4 py-3 text-sm text-n-slate-11">
                  <div class="flex flex-wrap gap-1">
                    <span
                      v-for="keyword in rule.keywords"
                      :key="keyword"
                      class="px-2 py-0.5 rounded-lg bg-n-alpha-2 text-xs"
                    >
                      {{ keyword }}
                    </span>
                  </div>
                </td>
                <td class="px-4 py-3 text-sm">
                  <Switch
                    :model-value="rule.enabled"
                    :disabled="!!togglingIds[rule.id]"
                    @update:model-value="toggleEnabled(rule)"
                  />
                </td>
                <td class="px-4 py-3">
                  <div class="flex items-center justify-end gap-1">
                    <Button
                      icon="i-lucide-pencil"
                      variant="ghost"
                      color="slate"
                      size="sm"
                      :label="
                        t(
                          'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.LIST.EDIT'
                        )
                      "
                      @click="openEdit(rule)"
                    />
                    <Button
                      icon="i-lucide-trash-2"
                      variant="ghost"
                      color="ruby"
                      size="sm"
                      :is-loading="!!deletingIds[rule.id]"
                      :label="
                        t(
                          'INTEGRATION_SETTINGS.SOCIAL_COMMENT_AUTOMATIONS.LIST.DELETE'
                        )
                      "
                      @click="deleteRule(rule)"
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>
  </SettingsLayout>

  <SocialCommentAutomationDialog
    ref="dialogRef"
    :eligible-inboxes="eligibleInboxes"
    @saved="handleSaved"
  />
</template>
